/**
 * ParlaysFilterBar Component
 * Animated filter controls for the Parlays view
 * Uses framer-motion for smooth pill transitions
 */

import { SPORTSBOOKS } from '@config/constants';
import type {
  OriginFilter,
  ParlayStats,
  ResultFilter,
  TrackedParlay,
  TrackedParlaysFilters,
} from '@hooks/useTrackedParlays';
import type { League } from '@parlay-picker/shared/league';
import { SUPPORTED_LEAGUES } from '@parlay-picker/shared/league';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { memo, type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface ParlaysFilterBarProps {
  filters: TrackedParlaysFilters;
  onFiltersChange: (filters: TrackedParlaysFilters) => void;
  stats: ParlayStats;
  trackedParlays: TrackedParlay[];
  showComparison: boolean;
  onToggleComparison: () => void;
  leagueFilter: League | null;
  onLeagueFilterChange: (league: League | null) => void;
}

// =============================================================================
// Filter Chip (animated background on selection)
// =============================================================================

interface FilterChipProps {
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
  activeColor: string;
  icon?: string;
  dot?: string;
  testId?: string;
}

// Wrapped in memo to skip re-renders during the parlays-page render storm.
// Parent ParlaysView re-renders on every server fetch (filter change, live
// settlement, scoring update). Without memo, every chip re-rendered on
// every parent render even when its own props were unchanged, which made
// Playwright's locator polling repeatedly miss-resolve the chip element
// during the 200ms AnimatePresence height-auto transition. With memo each
// chip only re-renders when its label/count/isActive/onClick actually
// change — the DOM stays stable and E2E doesn't need DOM-dispatch hacks.
const FilterChip = memo(function FilterChip({
  label,
  count,
  isActive,
  onClick,
  activeColor,
  icon,
  dot,
  testId,
}: FilterChipProps): ReactElement {
  return (
    <button
      type="button"
      data-testid={testId ?? `parlay-filter-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
      className={cn(
        'relative text-xs rounded-full font-medium transition-all flex items-center gap-1.5 px-4 py-2 md:px-3 md:py-1.5 min-h-11 md:min-h-0',
        isActive
          ? activeColor
          : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-active',
      )}
    >
      {dot && <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />}
      {icon && <span>{icon}</span>}
      <span className="relative z-10">{label}</span>
      {count != null && (
        <span
          className={cn(
            'relative z-10 text-[10px] px-1 rounded-full min-w-[16px] text-center',
            isActive ? 'bg-white/20' : 'bg-theme-border text-theme-text-tertiary',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
});

// =============================================================================
// Component
// =============================================================================

export function ParlaysFilterBar({
  filters,
  onFiltersChange,
  stats,
  trackedParlays,
  showComparison,
  onToggleComparison,
  leagueFilter,
  onLeagueFilterChange,
}: ParlaysFilterBarProps): ReactElement {
  // Derive filter sets
  const selectedResults = useMemo(
    () => new Set<ResultFilter>(filters.results || ['won', 'lost', 'pending', 'void']),
    [filters.results],
  );

  const selectedOrigins = useMemo(
    () => new Set<OriginFilter>(filters.origins || ['ai', 'manual', 'bet']),
    [filters.origins],
  );

  const selectedSportsbooks = useMemo(
    () => new Set<string>(filters.sportsbooks || []),
    [filters.sportsbooks],
  );

  // The league row shows only when the parlays span more than one league
  const leagueCount = useMemo(
    () => new Set(trackedParlays.map((parlay) => parlay.league)).size,
    [trackedParlays],
  );

  // Get unique sportsbooks from data. The selected books are folded in because
  // this filter is applied server-side: once one is active the fetch returns only
  // its parlays, so deriving purely from the data would drop every other chip and
  // leave no way to clear the filter.
  const uniqueSportsbooks = useMemo(() => {
    const books = new Set<string>(filters.sportsbooks?.map((b) => b.toLowerCase()) ?? []);
    for (const parlay of trackedParlays) {
      if (parlay.sportsbook) {
        books.add(parlay.sportsbook.toLowerCase());
      }
    }
    return Array.from(books).sort();
  }, [trackedParlays, filters.sportsbooks]);

  // Stable refs for the latest filter inputs. Toggle handlers below read
  // through these refs and have empty deps, so their identity is stable
  // across renders. This is what lets the memoized FilterChips actually
  // skip re-renders when their props haven't changed — without it, the
  // inline arrow `() => toggleResult('won')` creates a new function on
  // every parent render, defeating memo and producing the render storm
  // that kept tripping Playwright's locator polling.
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);
  const selectedResultsRef = useRef(selectedResults);
  const selectedOriginsRef = useRef(selectedOrigins);
  const selectedSportsbooksRef = useRef(selectedSportsbooks);
  useEffect(() => {
    filtersRef.current = filters;
    onFiltersChangeRef.current = onFiltersChange;
    selectedResultsRef.current = selectedResults;
    selectedOriginsRef.current = selectedOrigins;
    selectedSportsbooksRef.current = selectedSportsbooks;
  });

  const toggleResult = useCallback((filter: ResultFilter) => {
    const current = new Set(selectedResultsRef.current);
    if (current.has(filter)) {
      current.delete(filter);
    } else {
      current.add(filter);
    }
    if (current.size === 0) return;
    onFiltersChangeRef.current({
      ...filtersRef.current,
      results: Array.from(current) as ResultFilter[],
    });
  }, []);

  const toggleOrigin = useCallback((origin: OriginFilter) => {
    const current = new Set(selectedOriginsRef.current);
    if (current.has(origin)) {
      current.delete(origin);
    } else {
      current.add(origin);
    }
    if (current.size === 0) return;
    onFiltersChangeRef.current({
      ...filtersRef.current,
      origins: Array.from(current) as OriginFilter[],
    });
  }, []);

  const toggleSportsbook = useCallback((sportsbook: string) => {
    const current = new Set(selectedSportsbooksRef.current);
    if (current.has(sportsbook)) {
      current.delete(sportsbook);
    } else {
      current.add(sportsbook);
    }
    onFiltersChangeRef.current({
      ...filtersRef.current,
      sportsbooks: Array.from(current),
    });
  }, []);

  const clearSportsbooks = useCallback(() => {
    onFiltersChangeRef.current({ ...filtersRef.current, sportsbooks: [] });
  }, []);

  // Per-value handlers for chip onClick. With toggleResult/toggleOrigin
  // ref-stable above, these are stable for the lifetime of the component
  // — so memoized FilterChips don't re-render on irrelevant parent updates.
  const resultHandlers = useMemo(
    () => ({
      pending: () => toggleResult('pending'),
      won: () => toggleResult('won'),
      lost: () => toggleResult('lost'),
      void: () => toggleResult('void'),
    }),
    [toggleResult],
  );
  const originHandlers = useMemo(
    () => ({
      ai: () => toggleOrigin('ai'),
      manual: () => toggleOrigin('manual'),
      bet: () => toggleOrigin('bet'),
    }),
    [toggleOrigin],
  );

  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Count active filters for collapsed summary
  const activeFilterCount = useMemo(() => {
    const allResults: ResultFilter[] = ['won', 'lost', 'pending', 'void'];
    const allOrigins: OriginFilter[] = ['ai', 'manual', 'bet'];
    const resultsDiff = allResults.length - (filters.results?.length ?? allResults.length);
    const originsDiff = allOrigins.length - (filters.origins?.length ?? allOrigins.length);
    const sbCount = filters.sportsbooks?.length ?? 0;
    return resultsDiff + originsDiff + sbCount + (leagueFilter ? 1 : 0);
  }, [filters, leagueFilter]);

  return (
    <div data-testid="parlays-filter-bar" className={cn('rounded-xl p-4 mb-6', 'bg-theme-surface')}>
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setFiltersExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between py-1 -my-1 rounded-lg hover:bg-theme-surface-hover active:bg-theme-surface-active transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-theme-text">Filters</span>
          {!filtersExpanded && activeFilterCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-theme-primary-fill text-theme-on-primary-fill font-medium">
              {activeFilterCount}
            </span>
          )}
        </div>
        {filtersExpanded ? (
          <ChevronUp size={16} className="text-theme-text-secondary" />
        ) : (
          <ChevronDown size={16} className="text-theme-text-secondary" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {filtersExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pt-3">
              {/* Row 1: Status filters */}
              <div>
                <div
                  className={cn(
                    'text-[10px] uppercase tracking-wider font-semibold mb-2',
                    'text-theme-text-tertiary',
                  )}
                >
                  Status
                </div>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    label="Pending"
                    count={stats.pending}
                    isActive={selectedResults.has('pending')}
                    onClick={resultHandlers.pending}
                    activeColor={'bg-theme-warning text-white'}
                    icon="⏳"
                  />
                  <FilterChip
                    label="Won"
                    count={stats.won}
                    isActive={selectedResults.has('won')}
                    onClick={resultHandlers.won}
                    activeColor={'bg-theme-success text-white'}
                    icon="✓"
                  />
                  <FilterChip
                    label="Lost"
                    count={stats.lost}
                    isActive={selectedResults.has('lost')}
                    onClick={resultHandlers.lost}
                    activeColor={'bg-theme-danger text-white'}
                    icon="✗"
                  />
                  {stats.voided > 0 && (
                    <FilterChip
                      label="Void"
                      count={stats.voided}
                      isActive={selectedResults.has('void')}
                      onClick={resultHandlers.void}
                      activeColor="bg-purple-500 text-white"
                      icon="⊘"
                    />
                  )}
                </div>
              </div>

              {/* Row 2: League + Origin + Sportsbook + Compare */}
              <div className="flex flex-wrap items-start gap-6">
                {/* League filters */}
                {leagueCount > 1 && (
                  <div>
                    <div
                      className={cn(
                        'text-[10px] uppercase tracking-wider font-semibold mb-2',
                        'text-theme-text-tertiary',
                      )}
                    >
                      League
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {SUPPORTED_LEAGUES.map((league) => (
                        <FilterChip
                          key={league}
                          label={league}
                          isActive={leagueFilter === league}
                          onClick={() =>
                            onLeagueFilterChange(leagueFilter === league ? null : league)
                          }
                          activeColor="bg-theme-primary-fill text-theme-on-primary-fill"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Origin filters */}
                <div>
                  <div
                    className={cn(
                      'text-[10px] uppercase tracking-wider font-semibold mb-2',
                      'text-theme-text-tertiary',
                    )}
                  >
                    Source
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip
                      label="AI"
                      count={stats.aiCount}
                      isActive={selectedOrigins.has('ai')}
                      onClick={originHandlers.ai}
                      activeColor="bg-blue-500 text-white"
                      icon="🤖"
                    />
                    <FilterChip
                      label="Custom"
                      count={stats.manualCount}
                      isActive={selectedOrigins.has('manual')}
                      onClick={originHandlers.manual}
                      activeColor={'bg-slate-500 text-white'}
                      icon="✏️"
                    />
                    <FilterChip
                      label="Real Bets"
                      count={stats.betCount}
                      isActive={selectedOrigins.has('bet')}
                      onClick={originHandlers.bet}
                      activeColor={'bg-cyan-500 text-white'}
                      icon="💰"
                    />
                  </div>
                </div>

                {/* Sportsbook filters. Also shown with a single option while a filter is
                    active: this filter runs server-side, so selecting a book shrinks the
                    data to one book, and hiding the section then stranded the user with
                    no control to clear it. */}
                {(uniqueSportsbooks.length > 1 || selectedSportsbooks.size > 0) && (
                  <div>
                    <div
                      className={cn(
                        'text-[10px] uppercase tracking-wider font-semibold mb-2',
                        'text-theme-text-tertiary',
                      )}
                    >
                      Sportsbook
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {uniqueSportsbooks.map((book) => {
                        const config = SPORTSBOOKS[book.toLowerCase()];
                        return (
                          <FilterChip
                            key={book}
                            label={config?.name || book}
                            isActive={selectedSportsbooks.has(book)}
                            onClick={() => toggleSportsbook(book)}
                            activeColor="bg-cyan-500 text-white"
                            dot={config?.dot}
                          />
                        );
                      })}
                      {selectedSportsbooks.size > 0 && (
                        <button
                          type="button"
                          onClick={clearSportsbooks}
                          className={cn(
                            'text-xs px-2 py-1 rounded-full transition',
                            'bg-theme-surface-hover text-theme-text-tertiary hover:bg-theme-surface-active',
                          )}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Compare Scores toggle */}
                <div className="ml-auto self-end">
                  <button
                    type="button"
                    onClick={onToggleComparison}
                    className={cn(
                      'text-xs px-3 py-1.5 rounded-full font-medium transition-all',
                      showComparison
                        ? 'bg-purple-500 text-white'
                        : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-active',
                    )}
                  >
                    {showComparison ? '✦ Comparing' : '⟷ Compare Scores'}
                  </button>
                </div>
              </div>

              {/* Disclaimer */}
              <p className={cn('text-[10px] italic', 'text-theme-text-tertiary')}>
                AI picks may contain errors. Past performance doesn't guarantee future results.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
